import { faCloudUploadAlt, faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../App.styles";
import ConfirmationModal from "../ConfirmationModal";
import Spinner from '../Spinner';
import useImageThumbnail from "../ImageThumbnail/useImageThumbnail";
import ImageThumbnailLoader from "../ImageThumbnail/ImageThumbnailLoader";
import { useUploadImageMutation } from "./NubImageUpload.state";
import {
    ActionWrapper,
    FileWrapper,
    ImageUploadWrapper,
    ImageContainer,
    ImageBody,
    UploadBody,
    UploadBtn,
    ImageLoader,
    ImagePreviewWrapper,
    ImageActionButton,
    ImageActionButtons,
    NubsItem,
} from "./NubImageUpload.style";
import { acceptedImages } from "../../consts/accepted-files";
import { toAssetUrl } from "../../lib/url";


// Component to render individual image using ImageThumbnailLoader
const NubImage = ({ url, isUploading, containerRef }) => {
    // Convert heic, dng, webp to jpg
    const imageUrl = (url || '').replace(/\.(heic|dng|webp)$/, '.jpg');
    const hookProps = useImageThumbnail({ thumb: imageUrl, isLoading: isUploading });
    const imageThumbnailLoaderProps = {
        isLoading: isUploading,
        className: 'nubs__image',
        width: 'full',
        height: 'full',
        src: imageUrl,
        alt: '',
        ...hookProps,
    };
    return (
        <NubsItem container={containerRef?.current} isImage={true}>
            <ImageThumbnailLoader {...imageThumbnailLoaderProps} />
        </NubsItem>
    );
};

const NubImageUpload = ({ label, name, useQuery, maxImages = 9 }) => {
    const { t } = useTranslation();
    const [collection, setCollection] = useQuery();
    const { id } = collection || {};
    const inputFileRef = useRef(null);
    const containerRef = useRef(null);
    
    const handleFileLabel = (e) => {
        if (e.key !== "Enter" || inputFileRef.current === null) return;
        inputFileRef.current.click();
    };

    const { isLoading: isUploading, mutateAsync: uploadImage } = useUploadImageMutation({ useQuery, name });

    // Parse comma-delimited image URLs
    const imageUrls = useMemo(() => {
        const value = collection?.[name];
        if (!value) return [];
        return value.split(',')
            // filter out invalid URLs
            .filter(url => url.trim())
            // reverse the array so the most recent images are at the left
            .reverse();
    }, [collection, name]);

    const upload = async (files) => {
        if (!files || files.length === 0) return;
        
        const filesToUpload = Array.from(files).slice(0, maxImages - imageUrls.length);
        
        for (const file of filesToUpload) {
            // Validate file type
            const ext = `.${file.name.split('.').pop().toLowerCase()}`;
            if (!acceptedImages.includes(ext)) {
                continue;
            }
            await uploadImage(file);
        }
    };

    const onDelete = async (urlToDelete) => {
        try {
            const updatedUrls = imageUrls.filter(url => url !== urlToDelete);
            await setCollection({
                [name]: updatedUrls.join(','),
            });
        } catch { }
    };

    const fileId = name && id ? `${name}-${id}-upload` : `new-upload-${id}`;
    const isDisabled = imageUrls.length >= maxImages;

    return (
        <ImageUploadWrapper className="form-outline">
            {label && <span className="form-label">{label}</span>}
            <ImageContainer ref={containerRef}>
                <ImageBody className="nubs__body" container={containerRef?.current}>
                    <UploadBody isDisabled={isDisabled} className="p-4" container={containerRef?.current} htmlFor={fileId} tabIndex={0} onKeyUp={handleFileLabel}>
                        <FontAwesomeIcon className="mb-4" icon={faCloudUploadAlt} />
                        <ActionWrapper>
                            <UploadBtn>
                                <FileWrapper>
                                    <input
                                        accept={acceptedImages.join(',')}
                                        className="form-control"
                                        id={fileId}
                                        type="file"
                                        multiple
                                        onChange={(evt) => {
                                            const { target } = evt;
                                            upload(target.files);
                                            // Reset input so same file can be selected again
                                            target.value = '';
                                        }}
                                        ref={inputFileRef}
                                    />
                                </FileWrapper>
                                <Button disabled={isDisabled} tag="span" color="primary" className="btn--flat">
                                    {t('Add Image')}
                                </Button>
                            </UploadBtn>
                        </ActionWrapper>
                        {imageUrls.length > 0 && (
                            <small className="text-muted">
                                {imageUrls.length} / {maxImages}
                            </small>
                        )}
                        {isDisabled && (
                            <small className="text-muted">
                                {t('Delete some images if you want to add more.')}
                            </small>
                        )}
                    </UploadBody>
                    
                    {imageUrls.map((url, index) => (
                        <ImagePreviewWrapper key={`${url}-${index}`}>
                            <ImageActionButtons className="image-action__buttons">
                                <ConfirmationModal
                                    title={t("Confirm Deletion")}
                                    body={t("Are you sure you wish to delete?")}
                                    confirmLabel={t("Delete")}
                                    confirmAction={() => onDelete(url)}
                                >
                                    <ImageActionButton 
                                        title={t(`Delete Image`)} 
                                        color="link" 
                                        tabIndex={-1} 
                                        type="button"
                                    >
                                        <FontAwesomeIcon icon={faTimesCircle} />
                                    </ImageActionButton>
                                </ConfirmationModal>
                            </ImageActionButtons>
                            <NubImage url={toAssetUrl(url)} isUploading={false} containerRef={containerRef} />
                        </ImagePreviewWrapper>
                    ))}
                </ImageBody>
            </ImageContainer>
            <ImageLoader isLoading={isUploading}>
                <Spinner />
            </ImageLoader>
        </ImageUploadWrapper>
    );
};

export default NubImageUpload;
